import { useEffect, useState } from 'react'
import { Grid2 as Grid, Paper, Stack, Typography, Box } from '@mui/material'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { analytics } from 'utils/analytics'
import { usePlatform } from 'hooks/usePlatform'
import { PRODUCTS, getSizeTierForCount } from './pricingData'
import type { ProductName } from './pricingData'
import EmployeeSlider from './EmployeeSlider'
import ProductCard from './ProductCard'

// ================================|| SELECT SUBSCRIPTION ||================================ //

function SelectSubscription() {
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { createCheckoutSession } = usePlatform('')

  // State from signup flow (may be null for standalone visits)
  const organizationId = location.state?.organizationId as string | undefined
  const stripeCustomerId = location.state?.stripeCustomerId as string | undefined

  // URL param preselection
  const preselectedProduct = searchParams.get('product') as ProductName | null
  const preselectedUsers = searchParams.get('users')

  const [employeeCount, setEmployeeCount] = useState(() => {
    if (preselectedUsers) {
      const parsed = parseInt(preselectedUsers, 10)
      if (!isNaN(parsed) && parsed >= 1 && parsed <= 300) return parsed
    }
    return 10
  })
  const [loadingProduct, setLoadingProduct] = useState<ProductName | null>(null)
  const [highlightedProduct] = useState<ProductName | null>(preselectedProduct)

  const sizeTier = getSizeTierForCount(employeeCount)

  useEffect(() => {
    analytics.track('subscription_selection_started')
  }, [])

  const handleSelect = async (product: ProductName) => {
    if (!organizationId || !stripeCustomerId) {
      // Missing org state — redirect to signup error
      navigate('/signup-error', {
        state: { message: 'Please complete signup before selecting a subscription.', code: 'missing_org_state' },
      })
      return
    }

    setLoadingProduct(product)

    try {
      const result = await createCheckoutSession({
        organizationId,
        product,
        sizeTier,
        stripeCustomerId,
      })

      analytics.track('subscription_checkout_started', {
        product,
        sizeTier,
        employeeCount,
      })

      // Redirect to Stripe Checkout
      window.location.href = result.checkoutUrl
    } catch (error) {
      console.error('Failed to create checkout session:', error)
      setLoadingProduct(null)
      navigate('/signup-error', {
        state: { message: 'Failed to start checkout. Please try again.', code: 'checkout_failed' },
      })
    }
  }

  const handleContactUs = () => {
    const subject = encodeURIComponent('Purcent Enterprise Inquiry')
    const body = encodeURIComponent(
      `Hi Purcent team,\n\nI'm interested in learning more about your enterprise plans.\n\nCompany size: ${employeeCount >= 300 ? '300+' : employeeCount} employees\n\nPlease get in touch at your earliest convenience.\n\nThank you`
    )
    window.location.href = `mailto:sales@purcent.com?subject=${subject}&body=${body}`
  }

  return (
    <Grid
      container
      spacing={3}
      sx={{ minHeight: '100vh', paddingTop: { xs: '2vh', md: '3vh' }, px: { xs: 2, sm: 3, md: 4 } }}
      justifyContent="center"
    >
      <Grid size={12}>
        <Paper
          variant="outlined"
          elevation={0}
          sx={(theme) => ({
            padding: { xs: 2, sm: 3 },
            borderRadius: 2,
            mx: 'auto',
            maxWidth: 1200,
            bgcolor: theme.palette.mode === 'dark' ? '#000' : 'background.paper',
          })}
        >
          <Stack spacing={3}>
            <Stack spacing={1} sx={{ textAlign: 'center' }}>
              <Box sx={{ mb: 1 }}>
                <img src="/PurcentLogo.svg" alt="logo" style={{ maxWidth: '160px', width: '100%', margin: '0 auto' }} />
              </Box>
              <Typography variant="h3" sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.25rem' } }}>
                Select Your Subscription
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                Choose the plan that best fits your needs. All subscriptions are billed annually.
              </Typography>
            </Stack>

            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
              <EmployeeSlider value={employeeCount} onChange={setEmployeeCount} />
            </Box>

            <Grid container spacing={{ xs: 2, sm: 3 }} justifyContent="center">
              {PRODUCTS.map((product) => (
                <Grid key={product.name} size={{ xs: 12, sm: 6, lg: 3 }}>
                  <ProductCard
                    product={product}
                    sizeTier={sizeTier}
                    highlighted={
                      highlightedProduct === product.name || (!highlightedProduct && product.name === 'limited')
                    }
                    loading={loadingProduct === product.name}
                    onSelect={handleSelect}
                    onContactUs={handleContactUs}
                  />
                </Grid>
              ))}
            </Grid>

            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', mt: 1 }}>
              * Smart Imports for Purcent Reward and Purcent Complete is limited to Total Reward Statement data imports
              only.
            </Typography>
          </Stack>
        </Paper>
      </Grid>
    </Grid>
  )
}

export default SelectSubscription
